import React, { useState, useLayoutEffect, ReactNode } from 'react';
import { Button, Form as AntForm, Upload } from 'antd';
import { AnySchema } from 'yup';
import { UploadFile, UploadListType } from 'antd/lib/upload/interface';
import fileSaver from 'file-saver';
import { PlusOutlined } from '@ant-design/icons';
import mime from 'mime';
import LightBox from 'react-image-lightbox';
import { formHelpers } from '@helpers/formHelpers';
import { readFileAsync } from '@lib/files';
import { FileToBeUploaded } from '@app-types/index';
import { UseFormMethods } from 'react-hook-form';
import uniqBy from 'lodash/uniqBy';

const { Item: FormItem } = AntForm;

const showUploadOptions = {
  showDownloadIcon: true,
  showPreviewIcon: true,
  showRemoveIcon: true,
};

interface HSMSUploadProps {
  label?: string;
  name: string;
  showUploadList?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: (ref: Element | null) => void;
  validationSchema?: AnySchema;
  setValue: UseFormMethods['setValue'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: any; // If we are setting proper types we need to get the form values. Not possible since we use this in multiple forms
  accept?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues: (payload?: { nest: boolean }) => any;
  listType?: UploadListType;
  regularButton?: boolean;
  buttonContent?: ReactNode | string;
  disabled?: boolean;
  className?: string;
  maxCount?: number;
}

export const HSMSFileUpload = ({
  label,
  name,
  validationSchema,
  showUploadList,
  register,
  errors,
  setValue,
  accept,
  getValues,
  listType = 'picture-card',
  regularButton,
  buttonContent,
  disabled,
  className,
  maxCount,
}: HSMSUploadProps) => {
  const [fieldValues, setFieldValues] = useState<string>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isInitialDataSet, setIsInitialDataSet] = useState<boolean>(false);
  const [preview, setPreview] = useState<{ previewImage: string; previewVisible: boolean }>({
    previewImage: '',
    previewVisible: false,
  });

  useLayoutEffect(() => {
    if (!fieldValues) {
      setFieldValues(getValues()[name] && JSON.parse(getValues()[name]));
    }
  });

  // If there's already files in the database, set them to the fileList here
  if (!isInitialDataSet && fieldValues && fieldValues.length > 0 && fileList.length === 0) {
    const existingFileList = JSON.parse(getValues()[name]).map((file: FileToBeUploaded) => {
      const fileName = file.url?.split('?')[0].split('/').pop();

      return {
        uid: file.id,
        name: fileName,
        status: 'done',
        url: file.url,
        size: 0,
        type: process.browser ? mime.getType(fileName || 'file') : 'file',
      };
    });

    setFileList(existingFileList);
    setIsInitialDataSet(true); // For safety. TODO: Remove after through testing
  }

  const uploadChildProps = () => {
    if (regularButton && buttonContent) {
      if (maxCount >= 0) {
        return fileList.length === 0 ? (
          <div>
            <PlusOutlined />
            <div className="ant-upload-text">Upload</div>
          </div>
        ) : null;
      }
      return <Button>{buttonContent}</Button>;
    }
    return (
      <div>
        <PlusOutlined />
        <div className="ant-upload-text">Upload</div>
      </div>
    );
  };

  return (
    <FormItem
      label={label}
      required={validationSchema && formHelpers.isRequired(validationSchema, name)}
      validateStatus={errors && errors[name] ? 'error' : ''}
      help={errors && errors[name]?.message}
      labelCol={{ span: 24 }}
      className={className}
    >
      <input type="text" hidden name={name} ref={register} />

      <Upload
        disabled={disabled}
        fileList={fileList}
        accept={accept}
        listType={listType}
        className="avatar-uploader"
        showUploadList={showUploadList ? showUploadOptions : showUploadList}
        maxCount={maxCount}
        method={undefined}
        customRequest={({ onSuccess, file }) => {
          setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onSuccess({ status: 'done' }, file);
          }, 0);
        }}
        onDownload={(fileData) => {
          const file = fileData.url || fileData.originFileObj;

          if (file) {
            fileSaver.saveAs(file, fileData.name);
          }
        }}
        onPreview={async (file) => {
          if (file.type.split('/')[0] === 'image') {
            let filePreview: string | null = null;

            if (!file.url && !file.preview) {
              filePreview = await readFileAsync(file.originFileObj as File);
            }

            setPreview({
              previewImage: file.url || filePreview || '',
              previewVisible: true,
            });
          } else {
            window.open(file.url);
          }
        }}
        onChange={async (info) => {
          const fileListToBeUploaded: FileToBeUploaded[] = [];

          await Promise.all(
            info.fileList.map(async (file) => {
              const fileData: FileToBeUploaded = {
                fileExtension: file.name?.split('.').pop() || '',
                contentType: file.type,
                id: file.uid,
              };

              // IF file is not removed
              if (file.status !== 'removed') {
                // For new files, get the data url. For already uploaded files, simply set the url
                if (!file.url) {
                  const fileURL = await readFileAsync(file.originFileObj as File);

                  fileData.fileURL = fileURL || undefined;
                } else {
                  fileData.url = file.url;
                }

                fileListToBeUploaded.push(fileData);
              }
            }),
          );

          setValue(name, JSON.stringify(fileListToBeUploaded)); // Set the form values
          const newFileList = [...fileList.filter((file) => file.status !== 'removed')];

          if (info.file.status !== 'removed') {
            newFileList.push({ ...info.file, status: 'done' });
          }

          setFileList(uniqBy(newFileList, 'uid')); //
        }}
      >
        {uploadChildProps()}
      </Upload>

      {preview.previewVisible && (
        <LightBox
          mainSrc={preview.previewImage}
          onCloseRequest={() => setPreview({ ...preview, previewVisible: false })}
        />
      )}
    </FormItem>
  );
};
